import { Box, Stack, Typography, useTheme } from '@mui/material';
import { type AutocompleteOption, FormikAutocompleteField } from '@vayufp/coast-components';
import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CreditCardBackground from './CreditCardBackground.svg';
import { previewCardValidationSchema } from '../../../../config/page-validation-schema';
import { MAX_NUM_CARDS, MIN_NUM_CARDS } from '../../../../domain/fleet';
import { StoreInfoSecurelyNote } from '../../../../general/components/store-info-securely-note';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';
import { RootState } from '../../../../store';
import { AddressFormFields } from '../../../address/components/address-form-fields';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { PreviewCardPayload, savePreviewCardInfo } from '../../reducers/business-info';
import { nextStep } from '../../reducers/next-step';

const selector = ({ businessInfo: { info }, nextStep: { loading } }: RootState) => ({
  saving: loading,
  businessInfo: info,
});

type Props = {
  isMobile: boolean;
};
const PreviewCardForm: FC<Props> = ({ isMobile }) => {
  const theme = useTheme();

  const dispatch = useDispatch();
  const [nextStepTrigger, setNextStepTrigger] = useState<'pre-order' | 'skip' | null>(null);
  const { businessInfo, saving } = useSelector(selector);

  const validationSchema = previewCardValidationSchema;

  const initialNumCards = businessInfo.numCardsToOrder || businessInfo.fleetSize || 5;
  const roundedNumCardsToOrder = Math.ceil(initialNumCards / 5) * 5;

  const cardRestrictionOptions: Array<AutocompleteOption<string>> = [
    {
      id: 'fuel_only',
      name: 'Fuel only at gas stations',
      description: 'When using this card at gas stations, you can only buy fuel.',
    },
    {
      id: 'fuel_and_maintenance',
      name: 'Fuel and maintenance items at gas stations',
      description:
        'When using this card at gas stations, you can buy fuel and vehicle-related expenses like car washes, vehicle parts and repair, and oil.',
    },
    {
      id: 'unrestricted',
      name: 'No restrictions at gas stations',
      description: 'When using this card at gas stations, you have no restrictions and can buy anything.',
    },
  ];

  const formik = useFormik({
    initialValues: {
      dbaName: businessInfo.dbaName || businessInfo.name,
      address: businessInfo.shippingAddress ?? businessInfo.address,
      numCardsToOrder: Math.min(roundedNumCardsToOrder, MAX_NUM_CARDS),
      cardRestriction: businessInfo.cardRestriction,
    },
    validationSchema,
    onSubmit: (values: PreviewCardPayload) => {
      setNextStepTrigger('pre-order');
      dispatch(savePreviewCardInfo({ ...values, dbaName: values.dbaName.trim() }));
    },
    validateOnChange: true,
    initialTouched: {
      dbaName: true,
      numCardsToOrder: true,
    },
    initialErrors: {
      dbaName: businessInfo.name.length > 24 ? 'Name on card must be less than 25 characters' : '',
    },
  });

  const handleSkip = () => {
    setNextStepTrigger('skip');
    dispatch(
      nextStep({
        action: 'preview_card_order',
        gotoNextPage: true,
      }),
    );
  };

  const trimmedDbaName = formik.values.dbaName.trim();

  const cardRestrictionHelperText = cardRestrictionOptions.find(
    (option) => option.id === (formik.values as PreviewCardPayload).cardRestriction,
  )?.description;

  return (
    <>
      {!isMobile && (
        <Box position="relative" display="flex" justifyContent="center">
          <img width="100%" alt="Sample Coast Card" src={CreditCardBackground} />
          <Typography
            variant="subtitle1-medium"
            color={theme.palette.primary.contrastText}
            sx={{
              position: 'absolute',
              top: '79%',
              left: '9%',
            }}
          >
            {trimmedDbaName.slice(0, 24)}
          </Typography>
        </Box>
      )}
      <form onSubmit={formik.handleSubmit}>
        <Stack gap={1} sx={{ mt: 5.5, mx: 'auto' }}>
          <Stack>
            <FormikTextField
              formik={formik}
              id="dbaName"
              name="dbaName"
              label="Company Name on Card"
              fullWidth
              data-public
            />
            <Stack direction="row" justifyContent="start">
              <Typography
                variant="caption"
                color={trimmedDbaName && trimmedDbaName.length > 24 ? 'error' : 'text.secondary'}
                sx={{ ml: 1.75 }}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {`${trimmedDbaName.length ?? 0} / 24 Characters`}
              </Typography>
            </Stack>
          </Stack>
          <FormikTextField
            formik={formik}
            id="numCardsToOrder"
            name="numCardsToOrder"
            label="Ship Upon Approval"
            type="number"
            placeholder="Cards"
            inputProps={{
              min: MIN_NUM_CARDS,
              max: MAX_NUM_CARDS,
              inputMode: 'numeric',
            }}
            data-public
          />
          <>
            <Stack gap={0.5}>
              <Typography variant="subtitle1-medium">Gas Station Restrictions</Typography>
              <Typography variant="body1">Choose what can be purchased at a gas station with these cards.</Typography>
            </Stack>
            <FormikAutocompleteField
              formik={formik}
              id="cardRestriction"
              name="cardRestriction"
              label="Gas Station Restrictions"
              options={cardRestrictionOptions}
              displayDescriptions={true}
              helperText={cardRestrictionHelperText}
              data-public
            />
          </>
          <Typography variant="subtitle1-medium" sx={{ my: 0.5 }}>
            Shipping Information
          </Typography>
          <AddressFormFields formik={formik} streetAddressLabelOverride="Ship Cards To" />
          <Stack gap={2}>
            <SubmitWorkflowButton
              type="submit"
              disabled={saving}
              progress={nextStepTrigger === 'pre-order' && saving}
              sx={{ mt: 5 }}
              disableNote={true}
            >
              Next
            </SubmitWorkflowButton>
            <SubmitWorkflowButton
              variant="outlined"
              disabled={saving}
              progress={nextStepTrigger === 'skip' && saving}
              disableNote={true}
              disableIcon={true}
              onClick={handleSkip}
            >
              Skip
            </SubmitWorkflowButton>
            <StoreInfoSecurelyNote />
          </Stack>
        </Stack>
      </form>
    </>
  );
};

export { PreviewCardForm };
